import { Navigate, useNavigate, useParams } from 'react-router';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { Box, Stack } from '@mui/material';
import { ReadOnlyTextField } from '../ui/material-ui/ReadOnlyTextField.tsx';
import {
    EntryWithoutExitRecodeCondition,
    RecodeErrorType,
    useRecodeEntryWithoutExitOutlet,
} from './CloudConnectorRecodeEntryWithoutExitConditionsOutlet.tsx';
import { ParkingPortalLayoutWithHeader } from '../mobile/layout/ParkingPortalLayoutWithHeader.tsx';
import {
    RequestStatus,
    useServerErrorEffect,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { Colors } from 'dg-web-shared/ui/vars.ts';
import {
    LoadingSpinnerPresets,
    PresetLoadingSpinner,
} from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';
import { RecodeConditionType } from 'dg-web-shared/model/RecodeCondition.ts';
import { BarrierGateIdentificationEnityValue } from '../offstreet-transactions/OperatorOffstreetTransactionShared.tsx';
import { useIsMobileRoute } from '../layout/components/BreadCrumb.tsx';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';

export function CloudConnectorRecodeEntryWithoutExitCondition() {
    const { recodeConditionId } = useParams<{ recodeConditionId: string }>();
    const {
        recodeConditions,
        refetchEntryWitoutExitDetailDesktop,
        offstreetTransactionSearchQuery,
    } = useRecodeEntryWithoutExitOutlet();
    const recodeCondition = recodeConditions.find(
        recodeCondition =>
            recodeCondition.recodeConditionId === recodeConditionId,
    );
    const isSingleCondition = recodeConditions.length === 1;
    return (
        <ParkingPortalLayoutWithHeader
            title={
                <Localized
                    de="Rabatt erfassen"
                    fr="Saisir rabais"
                    it="Registra ribasso"
                    en="Record discount"
                />
            }
            backTo={`${isSingleCondition ? '../..' : '..'}${offstreetTransactionSearchQuery}`}
        >
            <RecodeConditionBody
                recodeCondition={recodeCondition}
                refetchEntryWithoutExitDetailDesktop={
                    refetchEntryWitoutExitDetailDesktop
                }
                offstreetTransactionSearchQuery={
                    offstreetTransactionSearchQuery
                }
            />
        </ParkingPortalLayoutWithHeader>
    );
}

interface RecodeEntryWithoutExitPayload {
    recodeConditionId: string;
    entryWithoutExitId: number;
}

function RecodeConditionBody({
    recodeCondition,
    refetchEntryWithoutExitDetailDesktop,
    offstreetTransactionSearchQuery,
}: {
    recodeCondition?: EntryWithoutExitRecodeCondition;
    refetchEntryWithoutExitDetailDesktop?: () => void;
    offstreetTransactionSearchQuery: string;
}) {
    const [recodeTicketState, recodeTicket] = useServerWrite<
        RecodeEntryWithoutExitPayload,
        null,
        RecodeErrorType
    >(payload => ({
        url: `/ui-api/operator-account/cloud-connector/recode/entry-without-exit/${payload.entryWithoutExitId}`,
    }));

    const navigate = useNavigate();
    const isMobile = useIsMobileRoute();
    const {
        operatorData: { operatorExternalId },
    } = useOperatorContext();
    useServerSuccessEffect(recodeTicketState, () => {
        if (refetchEntryWithoutExitDetailDesktop) {
            refetchEntryWithoutExitDetailDesktop();
        }
        if (isMobile) {
            navigate(`/${operatorExternalId}/mobile/recode/success`);
        } else {
            navigate(`../../success${offstreetTransactionSearchQuery}`);
        }
    });
    useServerErrorEffect(recodeTicketState, (_, errorType) => {
        if (isMobile) {
            navigate(`/${operatorExternalId}/mobile/recode/error/${errorType}`);
        } else {
            navigate(
                `../../error/${errorType}${offstreetTransactionSearchQuery}`,
            );
        }
    });

    const pending = recodeTicketState.status === RequestStatus.PENDING;

    if (!recodeCondition) {
        return (
            <Navigate
                to={`../../error/${RecodeErrorType.ZONE_NOT_AVAILABLE}${offstreetTransactionSearchQuery}`}
            />
        );
    }
    return (
        <Box
            sx={{
                padding: 4,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                flexGrow: 1,
            }}
        >
            <Stack spacing={2}>
                <ReadOnlyTextField
                    label={
                        <Localized
                            de="Bedingung"
                            fr="Condition"
                            it="Condizione"
                            en="Condition"
                        />
                    }
                    value={
                        <Localized
                            {...recodeCondition.recodeConditionDescription}
                        />
                    }
                />
                <ReadOnlyTextField
                    label={
                        <Localized
                            de="Identifikation"
                            fr="Identification"
                            it="Identificazione"
                            en="Identification"
                        />
                    }
                    value={
                        <BarrierGateIdentificationEnityValue
                            barrierGateIdentificationEntity={
                                recodeCondition.barrierGateIdentificationEntity
                            }
                        />
                    }
                />
                <ReadOnlyTextField
                    label={
                        <Localized
                            de="Einfahrtszeit"
                            fr="Heure d'entrée"
                            it="Orario d'entrata"
                            en="Entry time"
                        />
                    }
                    value={Formatter.dayMonthYearHourMinute(
                        DateTime.fromISO(recodeCondition.entryDateTime),
                    )}
                />
                <ReadOnlyTextField
                    label={
                        <Localized de="Zone" fr="Zone" it="Zona" en="Zone" />
                    }
                    value={recodeCondition.zoneDescription}
                />
                <RecodeConditionTypeIdentification
                    recodeCondition={recodeCondition}
                />
            </Stack>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '48px',
                    fontSize: '16px',
                    marginTop: '10px',
                    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.30)',
                    background: pending
                        ? Colors.disabledButtonGrey
                        : Colors.grassGreen,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    color: Colors.white,
                    cursor: 'pointer',
                    '&:active': {
                        padding: '2px 0 0 2px',
                        backgroundColor: 'rgba(85, 196, 40, 0.8)',
                    },
                }}
                onClick={
                    !pending
                        ? () =>
                              recodeTicket({
                                  recodeConditionId:
                                      recodeCondition.recodeConditionId,
                                  entryWithoutExitId:
                                      recodeCondition.entryWithoutExitId,
                              })
                        : undefined
                }
            >
                {pending ? (
                    <PresetLoadingSpinner
                        preset={LoadingSpinnerPresets.EmbedInButton}
                    />
                ) : (
                    <Localized
                        de="Bestätigen"
                        fr="Confirmer"
                        it="Conferma"
                        en="Submit"
                    />
                )}
            </Box>
        </Box>
    );
}

function RecodeConditionTypeIdentification({
    recodeCondition,
}: {
    recodeCondition: EntryWithoutExitRecodeCondition;
}) {
    switch (recodeCondition.type) {
        case RecodeConditionType.TARIFF:
            return (
                <ReadOnlyTextField
                    label={
                        <Localized
                            de="Tarif"
                            fr="Tarif"
                            it="Tariffa"
                            en="Tariff"
                        />
                    }
                    value={recodeCondition.tariffName}
                />
            );
        case RecodeConditionType.FREE:
            return (
                <ReadOnlyTextField
                    label={<Localized de="Typ" fr="Type" it="Tipo" en="Type" />}
                    value={
                        <Localized
                            de="Gratis Ausfahrt"
                            fr="Sortie libre"
                            it="Uscita libera"
                            en="Free exit"
                        />
                    }
                />
            );
        case RecodeConditionType.AMOUNT:
            return (
                <ReadOnlyTextField
                    label={
                        <Localized
                            de="Rabatt"
                            fr="Rabais"
                            it="Sconto"
                            en="Discount"
                        />
                    }
                    value={`CHF ${recodeCondition.freeAmount}`}
                />
            );
        case RecodeConditionType.TIME:
            return (
                <ReadOnlyTextField
                    label={
                        <Localized
                            de="Gratis Zeit"
                            fr="Temps gratuit"
                            it="Tempo gratuito"
                            en="Free time"
                        />
                    }
                    value={
                        <Localized
                            de={`${recodeCondition.freeTimeMin} Minuten`}
                            fr={`${recodeCondition.freeTimeMin} minutes`}
                            it={`${recodeCondition.freeTimeMin} minuti`}
                            en={`${recodeCondition.freeTimeMin} minutes`}
                        />
                    }
                />
            );
    }
}
